import { SAFETY_POLICY_LABELS } from "@unbox/api";
import { ShieldTick } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { useInfiniteAssuranceVideos } from "@/api/assurance/assurance-videos";
import { useAssuranceVideo } from "@/api/assurance/video";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { FilterPills } from "@/components/filters/filter-pills";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import { getStandardRiskDescription, getStandardRiskExplanation } from "@/constants/risk-definitions";
import { getLogActionLabel, WITH_CREATOR_ACTIONS } from "@/lib/assurance/action-types";
import { useFilters } from "@/providers/filter-provider";
import { formatDate, formatPostedAgo } from "@/utils/date-conversion";
import { formatCreatorId, formatEvidence, formatViews } from "@/utils/formatters";
import { buildActivityItems } from "../../components/ActivityFeed";
import { DetailPanelLoadingSkeleton, FlaggedTabsLoadingSkeleton } from "../../components/FlaggedTabsLoadingSkeleton";
import { VideoProgressDetail } from "../../components/VideoProgressDetail";
import { buildFlaggedVideoParams } from "../../params";
import { type InProgressVideo, VideoProgressTable } from "./VideoProgressTable";

const dateFormatter = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });

// TikTok-notify actions are pure notifications (never a policy on their own in the
// new world), so they're hidden from the Policy & Status display. The API scopes this
// list to WITH_CREATOR_ACTIONS (contact_creator + trigger_sequence), so videos whose
// only action is one of these pure notifications are excluded.
const HIDDEN_POLICY_ACTIONS = ["demonetization_ticket", "takedown_request"];

type TabWithCreatorProps = {
  selectedId?: string;
  onSelectId?: (id: string | undefined) => void;
  search: string;
};

export const TabWithCreator = ({ selectedId, onSelectId, search }: TabWithCreatorProps) => {
  const { apiFilters, hasActiveFilters, reset } = useFilters();
  // Closing the detail panel (the X button) hides it; selecting a video reopens it.
  // Independent of which video is selected.
  const [detailOpen, setDetailOpen] = useState(true);

  const { videos, total, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteAssuranceVideos({
    ...buildFlaggedVideoParams(apiFilters, search),
    category: "actionInProgress",
    actionTaken: [...WITH_CREATOR_ACTIONS],
  });

  // TODO(UNB-1741): tidy this up — riskDetails/activity are hardcoded empty and only
  // hydrated for the selected video, and this list-to-view-model mapping is duplicated
  // across the flagged tabs. Pull it into a shared helper.
  const filtered: InProgressVideo[] = videos.map((v) => ({
    id: v.videoId,
    title: v.videoTitle,
    creator: formatCreatorId(v.creatorId ?? ""),
    creatorId: v.creatorId ?? undefined,
    views: formatViews(v.views),
    postedAgo: formatPostedAgo(v.publishDate),
    postedAtDate: dateFormatter.format(new Date(v.publishDate)),
    topFlags: v.topFlags ? { name: v.topFlags.name, severity: tierToSeverity(v.topFlags.tier ?? 4) } : null,
    flagCount: v.flagCount,
    urlFirstFrame: v.urlFirstFrame ?? null,
    isInAdCampaign: v.isInAdCampaign,
    policy: v.activePolicy
      ? SAFETY_POLICY_LABELS[v.activePolicy.type]
      : (() => {
          const visible = (v.actionTypes ?? []).filter((a) => !HIDDEN_POLICY_ACTIONS.includes(a));
          return visible.length ? getLogActionLabel(visible[0]) : "—";
        })(),
    nextScanDueAt: v.activePolicy?.nextScanDueAt ?? null,
    failed: false,
    riskDetails: [],
    activity: [],
    upcomingSteps: [],
  }));

  const resolvedId = selectedId ?? filtered[0]?.id ?? "";
  // No fallback to filtered[0] when selectedId is set: a deep-linked video not yet on a
  // loaded page would otherwise mix its own risks/activity/upcoming steps (fetched for
  // resolvedId below) with the first loaded row's header — two different videos stitched
  // together. isLoadingSelectedVideo covers that gap with a loading state instead.
  const selectedListItem = selectedId ? filtered.find((v) => v.id === selectedId) : filtered[0];
  const isLoadingSelectedVideo = !!selectedId && !selectedListItem && (hasNextPage || isFetchingNextPage);

  const { data: detail, isLoading: isDetailLoading } = useAssuranceVideo(resolvedId, {
    includeUpcomingPolicySteps: true,
  });

  // Deep-link support: when the URL targets a video on a not-yet-loaded page, keep
  // loading (bounded by the dataset) until it appears, instead of silently falling back
  // to the first video. Gated on `detail` so a stale/invalid id doesn't page through
  // the whole list.
  useEffect(() => {
    if (selectedId && detail && !videos.some((v) => v.videoId === selectedId) && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [selectedId, detail, videos, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const riskDetails = (detail?.flags ?? []).map((flag) => {
    const evidence = [
      ...formatEvidence(flag.transcriptEvidence, flag.transcriptEvidenceEn),
      ...formatEvidence(flag.visualEvidence, flag.visualEvidenceEn),
    ];
    if (evidence.length === 0)
      evidence.push({ timestamp: "", text: getStandardRiskExplanation(flag.riskTypeShort) ?? flag.explanation ?? "" });
    return {
      name: flag.riskTypeShort,
      severity: tierToSeverity(flag.riskTier ?? 4),
      description: getStandardRiskDescription(flag.riskId ?? "") ?? undefined,
      evidenceType: flag.riskType,
      confirmedBy: flag.edits[0]?.user ? `${flag.edits[0].user.firstName} ${flag.edits[0].user.lastName}` : undefined,
      confirmedAt: flag.edits[0]?.createdAt ? formatDate(flag.edits[0].createdAt) : undefined,
      evidence,
    };
  });

  const activity = buildActivityItems(detail);

  const detailVideo: InProgressVideo | undefined = selectedListItem
    ? {
        ...selectedListItem,
        riskDetails,
        activity,
        isQuarantined: detail?.quarantine?.isQuarantined,
        upcomingSteps: detail?.upcomingSteps ?? [],
      }
    : undefined;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 flex-1 min-h-0">
        <FilterPills className="-my-2" />
        <FlaggedTabsLoadingSkeleton
          gapClassName="gap-4"
          listClassName="w-[calc(57%-0.5rem)] shrink-0 h-full"
          detailClassName="w-[calc(43%-0.5rem)] h-full"
        />
      </div>
    );
  }

  const isTrulyEmpty = filtered.length === 0 && !hasActiveFilters && !search;

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <FilterPills className="-my-2" />
      {isTrulyEmpty ? (
        <div className="w-full flex-1 flex items-center justify-center rounded-md border border-border-primary bg-white">
          <UnboxTableEmptyStateContent
            rows={[]}
            emptyState={{
              icon: ShieldTick,
              title: "Brand safety achieved",
              description: (
                <>
                  All confirmed risks have been resolved.
                  <br />
                  Your creators are compliant and policies are working.
                </>
              ),
            }}
          />
        </div>
      ) : (
        <div className="flex flex-row gap-4 w-[100%] flex-1 min-h-0">
          <VideoProgressTable
            videos={filtered}
            total={total}
            selectedId={selectedListItem?.id ?? ""}
            onSelect={(id) => {
              onSelectId?.(id);
              setDetailOpen(true);
            }}
            hasActiveFilters={hasActiveFilters || !!search}
            onClearFilters={reset}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            fullWidth={!detailOpen}
          />
          {detailOpen &&
            (detailVideo ? (
              <VideoProgressDetail
                video={detailVideo}
                onClose={() => setDetailOpen(false)}
                isLoadingActivity={isDetailLoading}
              />
            ) : isLoadingSelectedVideo ? (
              <DetailPanelLoadingSkeleton className="w-[calc(43%-0.5rem)] h-full" />
            ) : (
              <div className="flex-1 flex items-center justify-center rounded-lg border border-border-primary bg-white min-h-[300px]">
                <UnboxTableEmptyStateContent rows={[]} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
